import * as React from "react";

type MediaGlassControlsProps = {
  kind: "video" | "audio";
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  muted: boolean;
  disabled?: boolean;
  showOverlayAction?: boolean;
  onPlayPause: () => void;
  onMuteToggle: () => void;
  onSeek: (nextSec: number) => void;
  onOverlay?: () => void;
  className?: string;
};

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return "0:00";
  const s = Math.floor(sec);
  const mm = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export default function MediaGlassControls({
  kind,
  isPlaying,
  currentTime,
  duration,
  muted,
  disabled = false,
  showOverlayAction = false,
  onPlayPause,
  onMuteToggle,
  onSeek,
  onOverlay,
  className = "",
}: MediaGlassControlsProps): JSX.Element {
  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const safeCurrentTime = Number.isFinite(currentTime) && currentTime > 0 ? Math.min(currentTime, safeDuration || currentTime) : 0;
  const isVideo = kind === "video";
  const scrubPct = safeDuration > 0 ? Math.max(0, Math.min(100, (safeCurrentTime / safeDuration) * 100)) : 0;

  return (
    <div
      className={cx(
        "rounded-xl",
        isVideo
          ? "border border-cyan-200/26 bg-[linear-gradient(180deg,rgba(10,24,34,0.5)_0%,rgba(7,14,24,0.68)_100%)] backdrop-blur-sm shadow-[inset_0_1px_0_rgba(180,240,255,0.08)] px-2.5 py-1.5"
          : "border border-white/10 bg-white/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] px-3 py-2",
        className,
      )}
    >
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onPlayPause}
          disabled={disabled}
          className={cx(
            "rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors",
            disabled
              ? "cursor-not-allowed border border-white/10 bg-white/5 text-white/40"
              : isVideo
                ? "border border-cyan-100/55 bg-cyan-300/20 text-cyan-50 hover:bg-cyan-300/28"
                : "border border-white/10 bg-white/12 text-white/95 hover:bg-white/18",
          )}
        >
          {isPlaying ? "Pause" : "Play"}
        </button>

        <div className="min-w-[76px] text-xs font-semibold tabular-nums text-white/95">
          {fmt(safeCurrentTime)} / {fmt(safeDuration)}
        </div>

        <div className="h-4 w-px bg-white/12" aria-hidden />

        <input
          type="range"
          min={0}
          max={Math.max(safeDuration, 0.001)}
          step={0.01}
          value={safeDuration > 0 ? safeCurrentTime : 0}
          onChange={(e) => onSeek(Number(e.currentTarget.value))}
          disabled={disabled || safeDuration <= 0}
          aria-label={`${kind} scrub`}
          className="h-1.5 flex-1 rounded-full accent-cyan-300"
          style={{
            background: `linear-gradient(90deg, rgba(34,211,238,0.95) 0%, rgba(125,211,252,0.92) ${scrubPct}%, rgba(255,255,255,0.22) ${scrubPct}%, rgba(255,255,255,0.22) 100%)`,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08), 0 0 8px rgba(34,211,238,0.2)",
          }}
        />

        <div className="h-4 w-px bg-white/12" aria-hidden />

        <button
          type="button"
          onClick={onMuteToggle}
          disabled={disabled}
          className={cx(
            "rounded-lg border border-white/10 px-2.5 py-1 text-xs font-medium",
            disabled ? "cursor-not-allowed bg-white/5 text-white/40" : "bg-white/10 text-white/92 hover:bg-white/16",
          )}
        >
          {muted ? "Unmute" : "Mute"}
        </button>

        {showOverlayAction && (
          <button
            type="button"
            onClick={onOverlay}
            disabled={disabled}
            className={cx(
              "rounded-lg border border-cyan-300/25 px-2.5 py-1 text-xs font-medium",
              disabled ? "cursor-not-allowed bg-cyan-300/5 text-cyan-100/40" : "bg-cyan-300/12 text-cyan-100 hover:bg-cyan-300/18",
            )}
          >
            Overlay
          </button>
        )}
      </div>
    </div>
  );
}
